import Clarity from '@microsoft/clarity';
import React, { Component, ReactNode, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { PostHogClickTracker, PostHogIdentify, PostHogPageTracker } from 'src/analytics';
import { pushDataLayerEvent } from 'src/analytics/gtm';
import { POSTHOG_KEY } from 'src/lib/posthog';

import { ClientProvider } from './context/ClientContext';
import { LegalResearchProvider } from './context/LegalResearchContext';
import { NavigationProvider } from './context/NavigationContext';
import { ProfileProvider, useProfile } from './context/ProfileContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ThemeProvider } from './context/ThemeContext';
import MainLayout from './pages/dashboard/MainLayout';
import MaintenanceGate from './shared/components/MaintenanceGate';
import ProtectedRoute from './shared/components/ProtectedRoute';
import { TEFProvider } from './tef/components';

type ChunkErrorBoundaryState = { hasError: boolean; error: Error | null };

class ChunkErrorBoundary extends Component<{ children: ReactNode }, ChunkErrorBoundaryState> {
  state: ChunkErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    const message = error?.message ?? '';
    const isChunkLoadError = /Loading chunk \d+ failed|ChunkLoadError|CSS_CHUNK_LOAD_FAILED/i.test(message);
    if (!isChunkLoadError) return;

    const reloadKey = 'tropel_chunk_reload_attempted';
    if (sessionStorage.getItem(reloadKey) === '1') return;
    sessionStorage.setItem(reloadKey, '1');
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
            <div className="text-slate-900 dark:text-slate-100 font-semibold text-lg">This page failed to load</div>
            <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              Please refresh the page. If this keeps happening, it may be caused by a blocked third-party script.
            </div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 w-full rounded-xl bg-slate-900 text-white py-2.5 text-sm font-semibold hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Login = React.lazy(() => import('./pages/login/Login'));
const SignUp = React.lazy(() => import('./pages/signup/SignUp'));
const AuthCallback = React.lazy(() => import('./pages/auth/AuthCallback'));
const ForgotPassword = React.lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/auth/ResetPassword'));
const Landing = React.lazy(() => import('./pages/landing/Landing'));
const AdminApp = React.lazy(() => import('./pages/admin/AdminApp'));
const SigningPage = React.lazy(() => import('./pages/signing/SigningPage'));
const NewsletterUnsubscribe = React.lazy(() => import('./pages/newsletter/NewsletterUnsubscribe'));
const FAQPage = React.lazy(() => import('./pages/faq/FAQPage'));
const BlogListPage = React.lazy(() => import('./pages/blog/BlogListPage'));
const BlogArticlePage = React.lazy(() => import('./pages/blog/BlogArticlePage'));
const ProductsPage = React.lazy(() => import('./pages/products/ProductsPage'));

const ScrollToTopOnRouteChange: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);
  return null;
};

// Component to handle route persistence
const RoutePersistence: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Save current route to localStorage
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated && location.pathname !== '/') {
      localStorage.setItem('lastRoute', location.pathname);
    }
  }, [location.pathname]);

  // Restore route on mount if user is authenticated
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const lastRoute = localStorage.getItem('lastRoute');

    if (isAuthenticated && lastRoute && location.pathname === '/') {
      // Only redirect if we're on the landing page and have a saved route
      navigate(lastRoute, { replace: true });
    }
  }, [navigate, location.pathname]);

  return null;
};

const ClarityIdentify: React.FC = () => {
  const { profile } = useProfile();
  const location = useLocation();
  useEffect(() => {
    if (profile?.id && typeof (window as unknown as Record<string, unknown>).clarity === 'function') {
      const friendlyName = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim() || undefined;
      Clarity.identify(profile.id, undefined, location.pathname, friendlyName);
    }
  }, [profile?.id, profile?.first_name, profile?.last_name, location.pathname]);
  return null;
};

const GTMRouteTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    pushDataLayerEvent('spa_route_change', {
      page_path: location.pathname,
      page_url: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname]);

  return null;
};

const ThemedRoutes: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ThemeProvider>
      <TEFProvider>{children}</TEFProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  const appContent = (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTopOnRouteChange />
      <RoutePersistence />
      <GTMRouteTracker />
      {POSTHOG_KEY ? (
        <>
          <PostHogPageTracker />
          <PostHogClickTracker />
          <PostHogIdentify />
        </>
      ) : null}
      <ChunkErrorBoundary>
        <Suspense fallback={<div className="min-h-screen" />}>
          <MaintenanceGate>
            <Routes>
              {/* Public Routes - Landing page with TEFProvider */}
              <Route
                path="/"
                element={
                  <ThemeProvider>
                    <TEFProvider>
                      <Landing />
                    </TEFProvider>
                  </ThemeProvider>
                }
              />

              {/* Auth routes - wrapped with theme */}
              <Route
                path="/login"
                element={
                  <ThemedRoutes>
                    <Login />
                  </ThemedRoutes>
                }
              />
              <Route
                path="/signup"
                element={
                  <ThemedRoutes>
                    <SignUp />
                  </ThemedRoutes>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <ThemedRoutes>
                    <ForgotPassword />
                  </ThemedRoutes>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <ThemedRoutes>
                    <ResetPassword />
                  </ThemedRoutes>
                }
              />
              <Route
                path="/auth/callback"
                element={
                  <ThemedRoutes>
                    <AuthCallback />
                  </ThemedRoutes>
                }
              />
              <Route
                path="/newsletter/unsubscribe"
                element={
                  <ThemedRoutes>
                    <NewsletterUnsubscribe />
                  </ThemedRoutes>
                }
              />

              <Route
                path="/faq"
                element={
                  <ThemeProvider>
                    <TEFProvider>
                      <FAQPage />
                    </TEFProvider>
                  </ThemeProvider>
                }
              />
              <Route
                path="/blog"
                element={
                  <ThemeProvider>
                    <TEFProvider>
                      <BlogListPage />
                    </TEFProvider>
                  </ThemeProvider>
                }
              />
              <Route
                path="/blog/:slug"
                element={
                  <ThemeProvider>
                    <TEFProvider>
                      <BlogArticlePage />
                    </TEFProvider>
                  </ThemeProvider>
                }
              />
              <Route
                path="/products"
                element={
                  <ThemeProvider>
                    <TEFProvider>
                      <ProductsPage />
                    </TEFProvider>
                  </ThemeProvider>
                }
              />

              {/* Public signing page - no auth, token-based */}
              <Route path="/sign/:token" element={<SigningPage />} />

              {/* Protected Routes - all app pages share the same layout */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <ThemedRoutes>
                      <ProfileProvider>
                        <SubscriptionProvider>
                          <ClarityIdentify />
                          <NavigationProvider>
                            <LegalResearchProvider>
                              <ClientProvider>
                                <MainLayout />
                              </ClientProvider>
                            </LegalResearchProvider>
                          </NavigationProvider>
                        </SubscriptionProvider>
                      </ProfileProvider>
                    </ThemedRoutes>
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route path="/admin/*" element={<AdminApp />} />
            </Routes>
          </MaintenanceGate>
        </Suspense>
      </ChunkErrorBoundary>
    </Router>
  );

  return appContent;
};

export default App;
